import HLS from 'hls.js';
import { AutoplayErrorDialog } from 'uicomponents';

import { Asset } from '../sidebar';
import { SimuliveVideoRoomContext } from './actions';
import { Broadcast } from '../event/types';

import analytics from '@/analytics';
import { HLS_PLAYBACK, getEnv } from '@/constants';
import DialogService from '@/services/dialogs/DialogService';
import { createHlsTrackers } from '@/services/hlsTrackers';
import { closeJoiningDelayNetworkErrorModal } from '@/services/liveMedia.service';
import loggerService from '@/services/logger.service';
import trackerService from '@/services/tracker.service';

let previousHlsLevel = -1; // tracks last quality level for downgrade detection
// Broadcast we have already raised a playback-watchdog alert for, so a
// purge/restart loop after the live window does not spam the error log.
let watchdogAlertedBroadcastId = '';
const hlsTrackers = createHlsTrackers('inHouseSimulive');

export function addHlsGuard(url: string, xhr: XMLHttpRequest, context: SimuliveVideoRoomContext) {
  const organizationId = context.rootState.event?.organization?.id || '';
  if (
    url.includes('.m3u8') &&
    context.rootState.event.useHlsGuard &&
    (url.includes('mediastore') ||
      url.includes('simulive.goldcast') ||
      url.includes('vid.goldcast') ||
      url.includes('simple-video-store'))
  ) {
    const token = context.rootState.user?.accessToken || '';
    const separator = url.includes('?') ? '&' : '?';
    let finalUrl = `${url}${separator}org_id=${organizationId}`;
    if (token) {
      finalUrl += `&code=${encodeURIComponent(token)}`;
    }
    let urlOrigin = '';
    try {
      urlOrigin = new URL(url).origin;
    } catch {
      urlOrigin = 'unparseable';
    }
    loggerService.info(
      `[simulive-debug] addHlsGuard rewriting m3u8 request: origin="${urlOrigin}", hasToken=${!!token}, hasOrgId=${!!organizationId}`
    );
    xhr.open('GET', finalUrl, true);
  }
}

export function playSimuliveUsingHLSJS(context: SimuliveVideoRoomContext) {
  const hlsInstance = new HLS({
    ...context.state.hlsConfig,
    xhrSetup: function (xhr, url) {
      addHlsGuard(url, xhr, context);
    }
  });
  context.state.hLSContext = hlsInstance;
  const hlsId = Date.now();
  loggerService.info(`[simulive-debug] HLS instance created (id=${hlsId}), attaching event listeners`);
  attachHLSEventListeners(context, hlsId);
  const videoEl = context.state.hlsVideoElement;
  loggerService.info(
    `[simulive-debug] Calling attachMedia (id=${hlsId}), videoElement exists: ${!!videoEl}, isConnected=${videoEl?.isConnected}, hasParent=${!!videoEl?.parentElement}, width=${videoEl?.clientWidth}, height=${videoEl?.clientHeight}, muted=${videoEl?.muted}, readyState=${videoEl?.readyState}`
  );
  context.state.hLSContext.attachMedia(context.state.hlsVideoElement);
}

export function getInHouseSimuliveSource(broadcastDetails: Broadcast) {
  const serverUrl = getEnv('VUE_APP_INHOUSE_SIMULIVE_SOURCE');
  return `${serverUrl}simulive/${broadcastDetails.event}/${broadcastDetails.id}/hls.m3u8`;
}

export function getInHouseSimuliveSubManifestSource(broadcastDetails: Broadcast, bitrate: string) {
  const serverUrl = getEnv('VUE_APP_INHOUSE_SIMULIVE_SOURCE');
  return `${serverUrl}simulive/${broadcastDetails.event}/${broadcastDetails.id}/hls/${bitrate}.m3u8`;
}

export function attachHLSEventListeners(context: SimuliveVideoRoomContext, hlsId?: number) {
  context.state.hLSContext.on(HLS.Events.MEDIA_ATTACHED, () => {
    loggerService.info(
      `[simulive-debug] MEDIA_ATTACHED fired (hlsId=${hlsId}), hLSContext is same instance: ${context.state.hLSContext === context.state.hLSContext}`
    );
    loggerService.info(
      `[simulive-debug] hLSContext exists: ${!!context.state.hLSContext}, hLSVideoSource: "${context.state.hLSVideoSource}"`
    );
    loadSourceWithRetries(context);
  });

  context.state.hLSContext.on(HLS.Events.MANIFEST_PARSED, () => {
    loggerService.info(`[simulive-debug] MANIFEST_PARSED fired (hlsId=${hlsId}), starting video playback`);
    context.state.hlsVideoElement.muted = context.rootState.event.stage?.muted;
    playVideo(context);
    setupVideoPlaybackEndChecker(context);
  });

  context.state.hLSContext.on(HLS.Events.ERROR, (errorName: String, errorDetails: any) => {
    loggerService.info(
      `[simulive-debug] HLS ERROR (hlsId=${hlsId}): type=${errorDetails?.type}, details=${errorDetails?.details}, fatal=${errorDetails?.fatal}`
    );
    handleHLSErrors(context, errorDetails);
  });

  context.state.hLSContext.on(HLS.Events.LEVEL_SWITCHED, (event: any, data: any) => {
    const newLevel = data?.level ?? -1;
    const levels = (context.state.hLSContext as any)?.levels;
    const bitrateBps = levels?.[newLevel]?.bitrate ?? null;
    const isDowngrade = hlsTrackers.trackLevelSwitched(previousHlsLevel, newLevel);

    analytics.track('hls_level_switched', {
      broadcast_id: context.rootGetters['event/currentBroadcast']?.id,
      from_level: previousHlsLevel,
      to_level: newLevel,
      bitrate_bps: bitrateBps,
      is_downgrade: isDowngrade,
      timestamp: Date.now()
    });

    previousHlsLevel = newLevel;
  });

  context.state.hLSContext.on(HLS.Events.LEVEL_LOADED, () => {
    if (context.state.isHLSLevelLoadError) {
      context.commit('resetIsHLSLevelLoadError');
    }
  });

  context.state.hLSContext.on(HLS.Events.FRAG_LOADED, (event: any, data: any) => {
    const loadTime = (data?.stats?.loading?.end ?? 0) - (data?.stats?.loading?.start ?? 0);
    hlsTrackers.trackFragLoaded(loadTime);
    if (context.state.isHLSFragLoadError) {
      context.commit('resetIsHLSFragLoadError');
    }
  });

  context.state.hLSContext.on(HLS.Events.MANIFEST_LOADED, () => {
    if (context.state.isHLSManifestLoadError) {
      context.commit('resetIsHLSManifestLoadError');
    }
  });

  context.state.hLSContext.on(HLS.Events.FRAG_CHANGED, (e: any, data: any) => {
    context.state.currentFragementPosition = data?.frag?.start;
    const latency = context.state.hLSContext?.latency;
    if (latency !== undefined && latency >= 0) {
      trackerService.track('inHouseSimulive', 'liveLatency', { latency });
    }
  });

  context.state.hLSContext.on(HLS.Events.FPS_DROP, (event: any, data: any) => {
    const dropped = data?.currentDropped ?? 0;
    hlsTrackers.trackFpsDrop(dropped);
    analytics.track('hls_fps_drop', {
      broadcast_id: context.rootGetters['event/currentBroadcast']?.id,
      dropped_frames: dropped,
      total_dropped_frames: data?.totalDroppedFrames ?? 0,
      timestamp: Date.now()
    });
  });
}

export function playVideo(context: SimuliveVideoRoomContext) {
  const videoEl = context.state.hlsVideoElement;
  const stageMutedAtPlay = context.rootState.event.stage?.muted;
  const userActivation = (navigator as any).userActivation;
  loggerService.info(
    `[simulive-debug] playVideo invoking play(): stage.muted=${stageMutedAtPlay}, videoEl.muted=${videoEl?.muted}, isConnected=${videoEl?.isConnected}, visibilityState=${document.visibilityState}, userActivation.isActive=${userActivation?.isActive}, userActivation.hasBeenActive=${userActivation?.hasBeenActive}, readyState=${videoEl?.readyState}, networkState=${videoEl?.networkState}`
  );
  videoEl
    .play()
    .catch((err: any) => {
      loggerService.error('Error playing video!', err);
      loggerService.warn(
        `[simulive-debug] play() rejected: errorName=${err?.name}, errorMessage=${err?.message}, stage.muted=${context.rootState.event.stage?.muted}, videoEl.muted=${videoEl?.muted}, videoEl.error.code=${videoEl?.error?.code}`
      );
      if (context.rootState.event.stage?.muted) {
        loggerService.warn(
          '[simulive-debug] playVideo bailing silently — stage was already muted at first rejection, no retry or autoplay dialog will fire'
        );
        return;
      }
      context.commit('event/stage/toggleMuted', null, {
        root: true
      });
      context.state.hlsVideoElement.muted = true;
      loggerService.info('[simulive-debug] playVideo: retrying play() after forcing muted=true');

      context.state.hlsVideoElement
        .play()
        .then(() => {
          loggerService.info('[simulive-debug] playVideo: muted-retry succeeded, showing AutoplayErrorDialog');
          const res = DialogService.show(AutoplayErrorDialog);
          res.promise
            .then(() => {
              if (context.rootState.event.stage?.muted) {
                context.commit('event/stage/toggleMuted', null, {
                  root: true
                });
              }
            })
            .catch(err => {
              loggerService.error('Error while toggeling mute state!', err);
            });
        })
        .catch((err: any) => {
          loggerService.error('Error playing video on retry after audio mute!', err);
          loggerService.warn(
            `[simulive-debug] playVideo muted-retry also rejected: errorName=${err?.name}, errorMessage=${err?.message} — falling back to controls=true + poster`
          );
          context.commit('event/stage/toggleMuted', null, {
            root: true
          });
          context.state.hlsVideoElement.controls = true;
          context.state.hlsVideoElement.poster = context.rootGetters['liveVideoRoom/fillerSlideUrl'];
        });
    })
    .finally(() => {
      context.state.isFillerSlideVisible = false;
    });
}

export function addVideoPlaybackListeners(context: SimuliveVideoRoomContext) {
  if (!context.state.hlsVideoElement) {
    return;
  }

  // ── Buffer tracking ──────────────────────────────────────────────────────
  context.state.hlsVideoElement.onwaiting = () => {
    loggerService.info('Simulive video room waiting on buffer!');
    const now = Date.now();
    hlsTrackers.trackBufferStart(now);
    // Real-time Segment event for buffer spike with context at the moment of stall
    analytics.track('hls_buffer_start', {
      broadcast_id: context.rootGetters['event/currentBroadcast']?.id,
      timestamp: now,
      current_latency_s: context.state.hLSContext?.latency ?? null,
      current_bandwidth_bps: (context.state.hLSContext as any)?.bandwidthEstimate ?? null
    });
    handleVideoPlaybackBuffering(context);
    handleVideoPlaybackStallRestart(context);
  };

  context.state.hlsVideoElement.oncanplay = () => {
    loggerService.info('Simulive video room can play video, resuming!');
    // Track bufferEnd here (original position). Also tracked in onplaying as a safety net
    // because some browsers skip oncanplay after the first buffer and go straight to onplaying.
    // The bufferStart > 0 guard in trackerService prevents double-counting.
    hlsTrackers.trackBufferEnd();
    handleVideoPlaybackResume(context);
  };

  context.state.hlsVideoElement.onended = () => {
    loggerService.info('Playback ended destroying!!');
    const currentBroadcast = context.rootGetters['event/currentBroadcast'];
    const isTestSimuliveRunning = currentBroadcast.simulive_test_run_end_time;
    if (isTestSimuliveRunning) {
      context.dispatch('destroySimuliveBroadcastPlaybackContext');
      return;
    }
    context.dispatch('purgeAndStartVideoPlaybackAgain');
  };

  context.state.hlsVideoElement.onplay = () => {
    context.state.hlsVideoElement.controls = false;
    analytics.track('hls_user_action', {
      action: 'play',
      broadcast_id: context.rootGetters['event/currentBroadcast']?.id,
      position_s: context.state.hlsVideoElement?.currentTime ?? null,
      timestamp: Date.now()
    });
  };

  context.state.hlsVideoElement.onplaying = () => {
    clearPlaybackWatchdogTimeout(context);
    resetWatchdogAlertedBroadcastId();
    loggerService.info('Simulive video started playing!');
    // Safety net: track bufferEnd here in case oncanplay was skipped by the browser.
    // bufferStart > 0 guard in trackerService prevents double-counting if oncanplay already fired.
    hlsTrackers.trackBufferEnd();
    hlsTrackers.trackConnectionEstablished();
    context.state.isFillerSlideVisible = false;
    context.state.networkErrorRetriesCount = 0;
    context.state.hlsVideoElement.controls = false;
    closeJoiningDelayNetworkErrorModal();
    if (!context.state.showOverlayCaptions) {
      const tracks = Array.from(context.state.hlsVideoElement?.textTracks) || [];
      tracks.forEach((track: any) => {
        track.value = track.language;
      });
      context.commit('setVttAvailableCaptions', tracks);
    }
  };

  context.state.hlsVideoElement.addEventListener('stalled', (event: any) => {
    loggerService.info('Playback stalled!', event);
  });
}

export function handleVideoPlaybackBuffering(context: SimuliveVideoRoomContext) {
  clearVideoPlaybackStallBufferTimeout(context);
  context.state.videoPlaybackStallTimeout = setTimeout(() => {
    context.state.hasVideoPlaybackStalled = true;
    clearVideoPlaybackStallBufferTimeout(context);
  }, HLS_PLAYBACK.WAIT_TIME_BEFORE_SHOWING_PLAYBACK_STALL_INDICATOR);
}

export function clearVideoPlaybackStallBufferTimeout(context: SimuliveVideoRoomContext) {
  if (!context.state.videoPlaybackStallTimeout) {
    return;
  }

  clearTimeout(context.state.videoPlaybackStallTimeout);
  context.state.videoPlaybackStallTimeout = null;
}

export function handleVideoPlaybackResume(context: SimuliveVideoRoomContext) {
  clearVideoPlaybackStallBufferTimeout(context);
  clearVideoPlaybackStallRestartTimeout(context);
  context.state.hasVideoPlaybackStalled = false;
}

export function handleHLSErrors(context: SimuliveVideoRoomContext, errorDetails: any) {
  const errorMessage = `
      Hls player encountered an error!,
      IS fatal: ${errorDetails?.fatal},
      Error details: 
    `;
  loggerService.warn('HLS playback errror:', errorMessage, errorDetails);

  // Fire real-time Segment event for every HLS error (fatal and non-fatal)
  analytics.track('hls_error', {
    broadcast_id: context.rootGetters['event/currentBroadcast']?.id,
    error_type: errorDetails?.type,
    error_details: errorDetails?.details,
    is_fatal: errorDetails?.fatal ?? false,
    timestamp: Date.now()
  });

  if (!errorDetails?.fatal) {
    loggerService.warn('Hls player encounted a non-fatal error!', errorDetails);
    hlsTrackers.trackNonFatalError();
    return;
  }

  if (errorDetails.type === HLS.ErrorTypes.NETWORK_ERROR) {
    handleNetworkError(context, errorDetails);
  } else if (errorDetails.type === HLS.ErrorTypes.MEDIA_ERROR) {
    trackerService.track('inHouseSimulive', 'error');
    handleMediaError(context, errorDetails);
  } else {
    trackerService.track('inHouseSimulive', 'error');
    context.dispatch('liveVideoRoom/showHlsVideoPlaybackError', null, {
      root: true
    });
  }
}

export function handleNetworkError(context: SimuliveVideoRoomContext, errorDetails: any) {
  hasSessionEnded(context);
  const wasA404Error = errorDetails?.response?.code === 404;
  if (wasA404Error) {
    if (errorDetails?.details === HLS.ErrorDetails.MANIFEST_LOAD_ERROR) {
      // Manifest 404s are expected at stream startup before the CDN publishes
      // the playlist; we retry transparently, so log as warn rather than error
      // to avoid flooding exception tracking with a recoverable condition.
      loggerService.warn('Manifest 404, retrying — stream not yet available', errorDetails);
      return handleManifestLoadError(context);
    }

    loggerService.error('404 error encountered while loading HLS video!', errorDetails);
    if (hasSessionEnded(context)) {
      return context.dispatch('purgeAndStartVideoPlaybackAgain');
    }

    trackerService.track('inHouseSimulive', 'error');
    return restartPlayback(context);
  }

  // Handling non 404 Errors
  handleGeneralNetworkError(context);

  if ([HLS.ErrorDetails.LEVEL_LOAD_ERROR, HLS.ErrorDetails.LEVEL_LOAD_TIMEOUT].includes(errorDetails?.details)) {
    context.commit('setIsHLSFragLoadError', true);
    return;
  }

  if ([HLS.ErrorDetails.MANIFEST_LOAD_ERROR, HLS.ErrorDetails.MANIFEST_LOAD_TIMEOUT].includes(errorDetails?.details)) {
    context.commit('setIsHLSManifestLoadError', true);
    if (navigator.onLine) {
      trackerService.track('inHouseSimulive', 'terminated', { isEnabled: true });
    }
    return;
  }

  if ([HLS.ErrorDetails.FRAG_LOAD_ERROR, HLS.ErrorDetails.FRAG_LOAD_TIMEOUT].includes(errorDetails?.details)) {
    context.commit('setIsHLSFragLoadError', true);
    if (navigator.onLine) {
      trackerService.track('inHouseSimulive', 'terminated', { isEnabled: true });
    }
    return;
  }
}

export function hasSessionEnded(context: SimuliveVideoRoomContext) {
  const broadcastDetails = context.rootGetters['event/currentEventBroadcast'](
    context.state.currentActiveSimuliveBroadcastID
  );
  const idealPlaybackEndTimestamp = getBroadcastVideoIdealPlaybackEndTimestamp(broadcastDetails);

  const hasEnded =
    idealPlaybackEndTimestamp &&
    context.rootState.event.stage?.heartBeatDate &&
    idealPlaybackEndTimestamp < context.rootState.event.stage.heartBeatDate.getTime();

  context.state.isSessionEnded = !!hasEnded;
  return hasEnded;
}
export function loadSourceWithRetries(context: SimuliveVideoRoomContext) {
  // Resolve the broadcast we are actively playing (single source of truth) rather than
  // the route-derived currentBroadcast, which can resolve to {} during navigation and
  // would wrongly drop us into the manifest-retry loop forever (stuck on the filler slide
  // with no loadSource call). This matches how hasSessionEnded resolves the broadcast.
  const currentBroadcast = context.rootGetters['event/currentEventBroadcast'](
    context.state.currentActiveSimuliveBroadcastID
  );
  const isLive = currentBroadcast?.is_live;
  const isTestSimuliveRunning = currentBroadcast?.simulive_test_run_end_time;
  loggerService.info(
    `[simulive-debug] loadSourceWithRetries: is_live=${isLive}, isTestSimuliveRunning=${isTestSimuliveRunning}, hLSContext exists=${!!context.state.hLSContext}, source="${context.state.hLSVideoSource}"`
  );
  if (isLive || isTestSimuliveRunning) {
    loggerService.info(`[simulive-debug] Calling hls.loadSource with: "${context.state.hLSVideoSource}"`);
    context.state.hLSContext.loadSource(context.state.hLSVideoSource);
  } else {
    loggerService.info(`[simulive-debug] is_live is false, entering retry loop via handleManifestLoadError`);
    handleManifestLoadError(context);
  }
}

export function handleManifestLoadError(context: SimuliveVideoRoomContext) {
  clearManifestLoadingErrorTimeout(context);
  context.state.manifestLoadingErrorHandlerTimeout = setTimeout(() => {
    loadSourceWithRetries(context);
    // loggerService.error('Manifest loading failed, retrying!!');
  }, HLS_PLAYBACK.WAIT_TIME_FOR_MANIFEST_LOADING_RETRIES);
}

export function clearManifestLoadingErrorTimeout(context: SimuliveVideoRoomContext) {
  if (!context.state.manifestLoadingErrorHandlerTimeout) {
    return;
  }
  clearTimeout(context.state.manifestLoadingErrorHandlerTimeout);
  context.state.manifestLoadingErrorHandlerTimeout = null;
}

export function handleGeneralNetworkError(context: SimuliveVideoRoomContext) {
  if (context.state.networkErrorRetriesCount > HLS_PLAYBACK.LEVEL_LOAD_MAX_RETIRES) {
    context.dispatch('liveVideoRoom/showHlsVideoPlaybackError', false, {
      root: true
    });
    return;
  }
  trackerService.track('inHouseSimulive', 'error');
  context.state.networkErrorRetriesCount = context.state.networkErrorRetriesCount + 1;
  restartPlayback(context);
}

export function restartPlayback(context: SimuliveVideoRoomContext) {
  trackerService.track('inHouseSimulive', 'playbackRestart');
  clearVideoPlaybackNudgeTimeout(context);
  context.state.videoPlaybackNudgeForwardTimeout = setTimeout(() => {
    context.dispatch('purgeAndStartVideoPlaybackAgain');
    clearVideoPlaybackNudgeTimeout(context);
  }, HLS_PLAYBACK.WAIT_TIME_BEFORE_NUDGING_PLAYBACK_FORWARD);
}

export function clearVideoPlaybackNudgeTimeout(context: SimuliveVideoRoomContext) {
  if (!context.state.videoPlaybackNudgeForwardTimeout) {
    return;
  }
  clearTimeout(context.state.videoPlaybackNudgeForwardTimeout);
  context.state.videoPlaybackNudgeForwardTimeout = null;
}

export function handleMediaError(context: SimuliveVideoRoomContext, errorDetails: any) {
  if (errorDetails?.details === HLS.ErrorDetails.BUFFER_STALLED_ERROR) {
    // Check if playback stalled due to a fragment that failed to load.
    // if so nudge the playback forward to start playing again.
    const hasFragFailedLoading = context.state.fragsThatFailedLoading.find(frag => {
      return frag?.start === context.state.currentFragementPosition;
    });
    if (hasFragFailedLoading) {
      restartPlayback(context);
      return;
    }
  }

  loggerService.warn('Fatal media error encountered, trying to recover!');
  context.state.hLSContext.recoverMediaError();
}

export function playSimuliveUsingNativeHLSPlayback(context: SimuliveVideoRoomContext) {
  context.state.isPlayingNatively = true;
  const videoEl = context.state.hlsVideoElement;
  videoEl.setAttribute('playsinline', '');
  videoEl.src = context.state.hLSVideoSource;
  videoEl.muted = context.rootState.event.stage?.muted;
  loggerService.info(
    `[simulive-debug] native HLS setup: isConnected=${videoEl.isConnected}, hasParent=${!!videoEl.parentElement}, muted=${videoEl.muted}, hasSource=${!!videoEl.src}`
  );

  videoEl.addEventListener('loadedmetadata', (event: Event) => {
    loggerService.info('[simulive-debug] native HLS loadedmetadata fired, calling playVideo');
    playVideo(context);
    setupVideoPlaybackEndChecker(context);
  });

  videoEl.addEventListener(
    'error',
    (err: Error) => {
      loggerService.error('Error playing on ios:', err);
      loggerService.warn(
        `[simulive-debug] native HLS error: videoEl.error.code=${videoEl.error?.code}, videoEl.error.message=${videoEl.error?.message}, networkState=${videoEl.networkState}, readyState=${videoEl.readyState}`
      );
      setTimeout(() => {
        context.dispatch('purgeAndStartVideoPlaybackAgain');
      }, 6000);
    },
    true
  );
}

export function getIdealTestRunEndTimeStamp(context: SimuliveVideoRoomContext, broadcastDetails: Broadcast) {
  const testRunEndTimeStamp = broadcastDetails.simulive_test_run_end_time
    ? new Date(broadcastDetails.simulive_test_run_end_time).getTime()
    : 0;

  const broadcastStartTimeStamp = broadcastDetails.start_time ? new Date(broadcastDetails.start_time).getTime() : 0;
  const timeWhenTestShouldForcedEnd =
    broadcastStartTimeStamp - HLS_PLAYBACK.BROADCAST_VIDEO_PLAYBACK_FORCED_END_DURATION;

  if (timeWhenTestShouldForcedEnd < testRunEndTimeStamp) {
    return timeWhenTestShouldForcedEnd;
  }

  return testRunEndTimeStamp;
}

export function getBroadcastVideoIdealPlaybackEndTimestamp(broadcastDetails: Broadcast) {
  const broadcastStartTime = broadcastDetails.live_start_time || broadcastDetails.start_time;

  const videoPlaybackStartTimeStamp = new Date(broadcastStartTime).getTime();

  const broadcastEndTime =
    broadcastDetails.live_end_time || broadcastDetails.allowed_end_time || broadcastDetails.expected_end_time || 0;

  const sessionEndTime = new Date(broadcastEndTime).getTime();

  const totalVideoDurationForBroadcastInSeconds = getTotalDurationOfAllVideoAssets(broadcastDetails.video_assets);

  const videoPlaybackEndTimeStamp = videoPlaybackStartTimeStamp + totalVideoDurationForBroadcastInSeconds * 1000;

  const sessionEndTimeWithSlowNetworkBuffer =
    sessionEndTime + HLS_PLAYBACK.MAX_ALLOWED_TIME_FOR_VIDEO_PLAYBACK_AFTER_SESSION;

  // If the computed end time of the session based on the
  // total video length and session start time exceeds
  // session end time + two minutes, use the session end time + two minutes for
  // ideal time calculation because the simulive server will stop responding
  // with manifest files after that time and we can consider the session has ended.
  if (sessionEndTimeWithSlowNetworkBuffer < videoPlaybackEndTimeStamp) {
    return sessionEndTimeWithSlowNetworkBuffer;
  }

  return videoPlaybackEndTimeStamp;
}

export function setupVideoPlaybackEndChecker(context: SimuliveVideoRoomContext) {
  clearVideoPlaybackEndInterval(context);

  context.state.intervalForCheckingVideoHasEnded = setInterval(() => {
    if (!context.state.hlsVideoElement) {
      return;
    }
    if (!context.state.idealPlaybackEndTimestamp) {
      return;
    }
    if (!context.rootState.event.stage?.heartBeatDate) {
      return;
    }

    const videoPlaybackEndTimeStampAccountingForBuffers =
      context.state.idealPlaybackEndTimestamp + HLS_PLAYBACK.MAX_ALLOWED_FALL_BEHIND_DURATION;

    // Not time for the video playback to end. Player will still buffer video.
    if (videoPlaybackEndTimeStampAccountingForBuffers > context.rootState.event.stage.heartBeatDate.getTime()) {
      return;
    }
    loggerService.info('Playback end caught by end checker destroying!!');
    // Because current time exceeded the ideal playback time stop playback.
    context.dispatch('purgeAndStartVideoPlaybackAgain');
  }, HLS_PLAYBACK.HLS_PLAYBACK_SYNC_INTERVAL);
}

export function clearVideoPlaybackEndInterval(context: SimuliveVideoRoomContext) {
  if (!context.state.intervalForCheckingVideoHasEnded) {
    return;
  }
  clearInterval(context.state.intervalForCheckingVideoHasEnded);
  context.state.intervalForCheckingVideoHasEnded = null;
}

export function getTotalDurationOfAllVideoAssets(videoAssets: Asset[] = []) {
  return videoAssets.reduce((prevValue: number, videoAsset: Asset) => {
    return prevValue + (videoAsset.duration || 0);
  }, 0);
}

export function handleVideoPlaybackStallRestart(context: SimuliveVideoRoomContext) {
  clearVideoPlaybackStallRestartTimeout(context);
  context.state.videoPlaybackStallRestartTimeout = setTimeout(() => {
    if (context.state.hasVideoPlaybackStalled) {
      loggerService.error('Video playback stalled for 12 seconds, restarting playback');
      restartPlayback(context);
    }
    clearVideoPlaybackStallRestartTimeout(context);
  }, 12000);
}

export function clearVideoPlaybackStallRestartTimeout(context: SimuliveVideoRoomContext) {
  if (!context.state.videoPlaybackStallRestartTimeout) {
    return;
  }
  clearTimeout(context.state.videoPlaybackStallRestartTimeout);
  context.state.videoPlaybackStallRestartTimeout = null;
}

export function resetWatchdogAlertedBroadcastId() {
  watchdogAlertedBroadcastId = '';
}

export function clearPlaybackWatchdogTimeout(context: SimuliveVideoRoomContext) {
  if (!context.state.playbackWatchdogTimeout) {
    return;
  }
  clearTimeout(context.state.playbackWatchdogTimeout);
  context.state.playbackWatchdogTimeout = null;
}

export function armPlaybackWatchdog(context: SimuliveVideoRoomContext) {
  clearPlaybackWatchdogTimeout(context);

  const currentBroadcast = context.rootGetters['event/currentBroadcast'];
  const liveStartTime = currentBroadcast?.live_start_time;
  if (!liveStartTime) {
    return;
  }

  const now = context.rootState.event.stage?.heartBeatDate?.getTime() ?? Date.now();
  const fireAt = new Date(liveStartTime).getTime() + HLS_PLAYBACK.SIMULIVE_PLAYBACK_WATCHDOG_DELAY;
  const delay = Math.max(0, fireAt - now);

  context.state.playbackWatchdogTimeout = setTimeout(() => {
    firePlaybackWatchdogCheck(context);
  }, delay);
}

export function firePlaybackWatchdogCheck(context: SimuliveVideoRoomContext) {
  clearPlaybackWatchdogTimeout(context);

  const broadcastId = context.state.currentActiveSimuliveBroadcastID;
  if (!broadcastId || watchdogAlertedBroadcastId === broadcastId) {
    return;
  }
  if (hasSessionEnded(context)) {
    return;
  }
  if (!context.state.isFillerSlideVisible) {
    return;
  }

  watchdogAlertedBroadcastId = broadcastId;

  const currentBroadcast = context.rootGetters['event/currentBroadcast'];
  const videoEl = context.state.hlsVideoElement;
  loggerService.error(
    `[simulive-debug] Playback watchdog: simulive session live but no video after ` +
      `${HLS_PLAYBACK.SIMULIVE_PLAYBACK_WATCHDOG_DELAY / 1000}s. ` +
      `broadcastId=${broadcastId}, broadcast_type=${currentBroadcast?.broadcast_type}, ` +
      `is_live=${currentBroadcast?.is_live}, live_start_time=${currentBroadcast?.live_start_time}, ` +
      `isFillerSlideVisible=${context.state.isFillerSlideVisible}, isSessionEnded=${context.state.isSessionEnded}, ` +
      `isPlayingNatively=${context.state.isPlayingNatively}, hLSVideoSource="${context.state.hLSVideoSource}", ` +
      `hLSContextExists=${!!context.state.hLSContext}, readyState=${videoEl?.readyState}, ` +
      `networkState=${videoEl?.networkState}, currentTime=${videoEl?.currentTime}, paused=${videoEl?.paused}, ` +
      `muted=${videoEl?.muted}, videoErrorCode=${videoEl?.error?.code}, isConnected=${videoEl?.isConnected}, ` +
      `onLine=${navigator.onLine}, visibilityState=${document.visibilityState}`
  );
}
